'use client';

import { useTranslations } from 'next-intl';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { AddressAutocomplete } from '@/components/checkout/AddressAutocomplete';
import { Input } from '@/components/ui/Input';
import { RadioInput } from '@/components/ui/RadioInput';
import { Select } from '@/components/ui/Select';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { useCountryAreaFieldConfig } from '@/hooks/useCountryAreaFieldConfig';
import { devLog } from '@/lib/utils/devLog';
import type { AddressValidationResult, ParsedAddress } from '@/lib/address/types';
import { MELISSA_RESULT_CODES } from '@/lib/address/types';
import {
  type BillingAddressFormData,
  COUNTRIES,
  getPrefectureDisplayName,
  isJapanLocale,
  JP_DELIVERY_TIME_SLOTS,
  JP_PREFECTURES,
  type JpFormData,
} from '@/lib/checkout';

import styles from '@/app/[lang]/(checkout)/store/checkout/delivery/page.module.scss';

interface TitleOption {
  value: string;
  label: string;
}

export interface MelissaValidationOutcome {
  isValid: boolean;
  correctedAddress?: ParsedAddress;
  generalErrors: string[];
}

export interface AddressFormHandle {
  validateWithMelissa: () => Promise<MelissaValidationOutcome>;
}

export interface AddressFormProps {
  formData: BillingAddressFormData;
  onInputChange: (field: keyof BillingAddressFormData, value: string) => void;
  formErrors: Record<string, string>;
  onSetFormErrors: (errors: Record<string, string>) => void;
  variant?: 'shipping' | 'billing';
  sectionTitle?: string;
  isJP?: boolean;
  jpFormData?: JpFormData;
  onJpInputChange?: (field: keyof JpFormData, value: string | boolean) => void;
  onJpPostalCodeChange?: (value: string) => void;
  isPostalCodeLoading?: boolean;
  titleOptions: TitleOption[];
  titleFieldLabel: string;
  title: string;
  onTitleChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  disableCountry?: boolean;
  showCountryArea?: boolean;
  showCountry?: boolean;
  footer?: React.ReactNode;
  lang: string;
  /** Renders without checkout section chrome; section title should be provided by the parent. */
  embedded?: boolean;
}

const MELISSA_ADDRESS_FIELDS = ['streetAddress1', 'postalCode', 'countryArea', 'city'] as const;

export const AddressForm = forwardRef<AddressFormHandle, AddressFormProps>(
  (
    {
      formData,
      onInputChange,
      formErrors,
      onSetFormErrors,
      variant = 'shipping',
      sectionTitle,
      isJP = false,
      jpFormData,
      onJpInputChange,
      onJpPostalCodeChange,
      isPostalCodeLoading,
      titleOptions,
      titleFieldLabel,
      title,
      onTitleChange,
      onAddressSelect,
      disableCountry,
      showCountryArea = true,
      showCountry = true,
      footer,
      lang,
      embedded = false,
    },
    ref
  ) => {
    const t = useTranslations('checkout');
    const { validate } = useAddressValidation();
    const countryArea = useCountryAreaFieldConfig(formData.country);
    const isBilling = variant === 'billing';
    const showJP = isJP && !isBilling;
    const autoCompletePrefix = isBilling ? 'billing ' : 'shipping ';
    const idPrefix = isBilling ? 'billing-' : '';

    // Keep a ref to the latest props so the imperative handle always reads fresh values
    const latestProps = useRef({ formData, isJP, onSetFormErrors });
    latestProps.current = { formData, isJP, onSetFormErrors };

    const setMelissaFieldErrors = useCallback((message: string) => {
      const fieldErrors: Record<string, string> = {};
      for (const field of MELISSA_ADDRESS_FIELDS) {
        fieldErrors[field] = message;
      }
      latestProps.current.onSetFormErrors(fieldErrors);
    }, []);

    const validateWithMelissa = useCallback(async (): Promise<MelissaValidationOutcome> => {
      const { formData: data, isJP: jp, onSetFormErrors: setErrors } = latestProps.current;

      devLog('[AddressForm] Melissa validation triggered', {
        country: data.country,
        isJP: jp,
        streetAddress1: data.streetAddress1,
        city: data.city,
        postalCode: data.postalCode,
      });

      if (jp) {
        devLog('[AddressForm] Skipping Melissa validation for JP');
        return { isValid: true, generalErrors: [] };
      }

      try {
        const result: AddressValidationResult = await validate({
          streetAddress1: data.streetAddress1,
          streetAddress2: data.streetAddress2 || undefined,
          city: data.city,
          postalCode: data.postalCode,
          countryArea: data.countryArea || undefined,
          country: data.country,
        });

        devLog('[AddressForm] Melissa result', {
          isValid: result.isValid,
          resultCodes: result.resultCodes,
          correctedAddress: result.correctedAddress,
          errors: result.errors,
        });

        if (!result.isValid) {
          const generalErrorCodes: string[] = [
            MELISSA_RESULT_CODES.GENERAL_ERROR,
            MELISSA_RESULT_CODES.NON_DELIVERABLE_ADDRESS,
            MELISSA_RESULT_CODES.MISSING_MINIMUM_ADDRESS,
            MELISSA_RESULT_CODES.LOCALITY_PARTIAL,
          ];
          const hasGeneralError = result.resultCodes.some((code) =>
            generalErrorCodes.includes(code)
          );

          if (hasGeneralError) {
            setMelissaFieldErrors('Please check address items and try again.');
            return { isValid: false, generalErrors: [] };
          }

          if (result.resultCodes.includes(MELISSA_RESULT_CODES.UNKNOWN_STREET)) {
            setErrors({ streetAddress1: 'Invalid street name' });
            return { isValid: false, generalErrors: [] };
          }

          const fieldErrors: Record<string, string> = {};
          const generalErrors: string[] = [];
          result.errors.forEach((error) => {
            if (error.field === 'general') {
              generalErrors.push(error.message);
            } else {
              fieldErrors[error.field] = error.message;
            }
          });

          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
          } else if (generalErrors.length > 0) {
            setMelissaFieldErrors(generalErrors[0]);
          }

          return { isValid: false, generalErrors };
        }

        return {
          isValid: true,
          correctedAddress: result.correctedAddress,
          generalErrors: [],
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to validate address';
        setMelissaFieldErrors(message);
        return { isValid: false, generalErrors: [message] };
      }
    }, [validate, setMelissaFieldErrors]);

    useImperativeHandle(
      ref,
      () => ({
        validateWithMelissa,
      }),
      [validateWithMelissa]
    );

    const heading =
      sectionTitle ?? (isBilling ? t('delivery.billingAddress') : t('delivery.deliveryAddress'));
    const Wrapper = embedded ? 'div' : 'section';
    const wrapperClassName = embedded ? styles.embedded : styles.section;

    let countryAreaField: React.ReactNode = null;
    if (showCountryArea) {
      countryAreaField = countryArea.hasChoices ? (
        <Select
          id={`${idPrefix}countryArea`}
          placeholder={countryArea.label}
          value={formData.countryArea ?? ''}
          onChange={(e) => onInputChange('countryArea', e.target.value)}
          error={formErrors.countryArea}
          autoComplete={`${autoCompletePrefix}address-level1`}
          options={countryArea.options}
          required={countryArea.isRequired}
          fullWidth
        />
      ) : (
        <Input
          id={`${idPrefix}countryArea`}
          placeholder={countryArea.label}
          value={formData.countryArea}
          onChange={(e) => onInputChange('countryArea', e.target.value)}
          error={formErrors.countryArea}
          autoComplete={`${autoCompletePrefix}address-level1`}
          required={countryArea.isRequired}
          fullWidth
        />
      );
    }

    return (
      <Wrapper className={wrapperClassName}>
        {!embedded ? <h2 className={styles.sectionTitle}>{heading}</h2> : null}

        {showJP && jpFormData && onJpInputChange && onJpPostalCodeChange ? (
          <>
            {/* Salutation (Male / Female) */}
            <fieldset className={styles.field} data-testid="jp-salutation">
              <legend className={styles.label}>
                {t('delivery.salutation')} <span className={styles.required}>*</span>
              </legend>
              <div className={styles.row}>
                <RadioInput
                  name="salutation"
                  value="male"
                  label={t('delivery.male')}
                  checked={jpFormData.salutation === 'male'}
                  onChange={() => onJpInputChange('salutation', 'male')}
                />
                <RadioInput
                  name="salutation"
                  value="female"
                  label={t('delivery.female')}
                  checked={jpFormData.salutation === 'female'}
                  onChange={() => onJpInputChange('salutation', 'female')}
                />
              </div>
              {formErrors.salutation && (
                <span className={styles.error}>{formErrors.salutation}</span>
              )}
            </fieldset>

            {/* Name — reversed order for JP (last name first) */}
            <div className={styles.row}>
              <Input
                id="lastName"
                placeholder={t('delivery.lastNameKanji')}
                value={formData.lastName}
                onChange={(e) => onInputChange('lastName', e.target.value)}
                error={formErrors.lastName}
                autoComplete="family-name"
                required
                fullWidth
              />
              <Input
                id="firstName"
                placeholder={t('delivery.firstNameKanji')}
                value={formData.firstName}
                onChange={(e) => onInputChange('firstName', e.target.value)}
                error={formErrors.firstName}
                autoComplete="given-name"
                required
                fullWidth
              />
            </div>

            {/* Katakana Name Fields */}
            <div className={styles.row}>
              <Input
                id="lastNameKatakana"
                data-testid="jp-last-name-katakana"
                label={t('delivery.lastNameKatakana')}
                value={jpFormData.lastNameKatakana}
                onChange={(e) => onJpInputChange('lastNameKatakana', e.target.value)}
                error={formErrors.lastNameKatakana}
                placeholder="ヤマダ"
                required
                fullWidth
              />
              <Input
                id="firstNameKatakana"
                data-testid="jp-first-name-katakana"
                label={t('delivery.firstNameKatakana')}
                value={jpFormData.firstNameKatakana}
                onChange={(e) => onJpInputChange('firstNameKatakana', e.target.value)}
                error={formErrors.firstNameKatakana}
                placeholder="タロウ"
                required
                fullWidth
              />
            </div>

            {/* Romaji Name Fields */}
            <div className={styles.row}>
              <Input
                id="lastNameRomaji"
                data-testid="jp-last-name-romaji"
                label={t('delivery.lastNameRomaji')}
                value={jpFormData.lastNameRomaji}
                onChange={(e) => onJpInputChange('lastNameRomaji', e.target.value)}
                error={formErrors.lastNameRomaji}
                placeholder="Yamada"
                required
                fullWidth
              />
              <Input
                id="firstNameRomaji"
                data-testid="jp-first-name-romaji"
                label={t('delivery.firstNameRomaji')}
                value={jpFormData.firstNameRomaji}
                onChange={(e) => onJpInputChange('firstNameRomaji', e.target.value)}
                error={formErrors.firstNameRomaji}
                placeholder="Taro"
                required
                fullWidth
              />
            </div>

            <input type="hidden" id="country" value="JP" />

            {/* Postal Code with auto-lookup */}
            <div className={styles.field}>
              <div className={styles.row}>
                <Input
                  id="postalCode"
                  data-testid="jp-postal-code"
                  placeholder={t('delivery.postalCode')}
                  value={formData.postalCode}
                  onChange={(e) => onJpPostalCodeChange(e.target.value)}
                  error={formErrors.postalCode}
                  autoComplete="postal-code"
                  required
                  fullWidth
                />
                {isPostalCodeLoading && (
                  <span data-testid="postal-code-loading" className={styles.loadingIndicator}>
                    …
                  </span>
                )}
              </div>
            </div>

            {/* Prefecture Dropdown */}
            <Select
              id="countryArea"
              label={t('delivery.prefecture')}
              value={formData.countryArea ?? ''}
              onChange={(e) => onInputChange('countryArea', e.target.value)}
              error={formErrors.countryArea}
              autoComplete="address-level1"
              options={JP_PREFECTURES.map((pref) => ({
                value: pref.code,
                label: getPrefectureDisplayName(pref, lang),
              }))}
              required
              fullWidth
            />

            <Input
              id="city"
              placeholder={t('delivery.cityWardTown')}
              value={formData.city}
              onChange={(e) => onInputChange('city', e.target.value)}
              error={formErrors.city}
              autoComplete="address-level2"
              required
              fullWidth
            />

            <Input
              id="streetAddress1"
              placeholder={t('delivery.chomeBanchiBango')}
              value={formData.streetAddress1}
              onChange={(e) => onInputChange('streetAddress1', e.target.value)}
              error={formErrors.streetAddress1}
              autoComplete="address-line1"
              required
              fullWidth
            />

            <Input
              id="houseName"
              placeholder={t('delivery.houseName')}
              value={jpFormData.houseName}
              onChange={(e) => onJpInputChange('houseName', e.target.value)}
              autoComplete="address-line2"
              fullWidth
            />

            <Input
              id="apartmentNumber"
              placeholder={t('delivery.apartmentNumber')}
              value={jpFormData.apartmentNumber}
              onChange={(e) => onJpInputChange('apartmentNumber', e.target.value)}
              fullWidth
            />

            <Input
              type="tel"
              id="phone"
              label={t('delivery.phone')}
              value={formData.phone}
              onChange={(e) => onInputChange('phone', e.target.value)}
              error={formErrors.phone}
              autoComplete="tel"
              placeholder={t('delivery.phonePlaceholder')}
              required
              fullWidth
            />

            {/* Delivery Time Slot */}
            <Select
              id="deliveryTimeSlot"
              placeholder={t('delivery.deliveryTimeSlot')}
              value={jpFormData.deliveryTimeSlot}
              onChange={(e) => onJpInputChange('deliveryTimeSlot', e.target.value)}
              options={JP_DELIVERY_TIME_SLOTS.map((slot) => ({
                value: slot.value,
                label: isJapanLocale(lang) ? slot.label : slot.latinLabel,
              }))}
              fullWidth
            />

            <label className={styles.checkbox} data-testid="jp-join-club">
              <input
                type="checkbox"
                checked={jpFormData.joinClub}
                onChange={(e) => onJpInputChange('joinClub', e.target.checked)}
              />
              <span>{t('delivery.joinClub')}</span>
            </label>

            <label className={styles.checkbox} data-testid="jp-include-receipt">
              <input
                type="checkbox"
                checked={jpFormData.includeReceipt}
                onChange={(e) => onJpInputChange('includeReceipt', e.target.checked)}
              />
              <span>{t('delivery.includeReceipt')}</span>
            </label>
          </>
        ) : (
          <>
            <Select
              id={`${idPrefix}title`}
              placeholder={titleFieldLabel}
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              error={formErrors.title}
              options={titleOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              required
              fullWidth
            />

            <div className={styles.row}>
              <Input
                id={`${idPrefix}firstName`}
                placeholder={t('delivery.firstName')}
                value={formData.firstName}
                onChange={(e) => onInputChange('firstName', e.target.value)}
                error={formErrors.firstName}
                autoComplete={`${autoCompletePrefix}given-name`}
                required
                fullWidth
              />
              <Input
                id={`${idPrefix}lastName`}
                placeholder={t('delivery.lastName')}
                value={formData.lastName}
                onChange={(e) => onInputChange('lastName', e.target.value)}
                error={formErrors.lastName}
                autoComplete={`${autoCompletePrefix}family-name`}
                required
                fullWidth
              />
            </div>

            <AddressAutocomplete
              value={formData.streetAddress1}
              onChange={(value) => onInputChange('streetAddress1', value)}
              onSelect={onAddressSelect}
              countryCode={formData.country}
              label={t('delivery.findYourAddress')}
              hint={t('delivery.noShipPoBox')}
              placeholder={t('delivery.streetAddressPlaceholder')}
              error={formErrors.streetAddress1}
              required
              name={`${idPrefix}streetAddressAutocomplete`}
            />

            <Input
              id={`${idPrefix}companyName`}
              placeholder={t('delivery.companyName')}
              value={formData.companyName || ''}
              error={formErrors.companyName}
              onChange={(e) => onInputChange('companyName', e.target.value)}
              autoComplete={`${autoCompletePrefix}organization`}
              fullWidth
            />
            <div className={styles.row}>
              <Input
                id={`${idPrefix}streetAddress1`}
                placeholder={t('delivery.streetAddress1')}
                value={formData.streetAddress1}
                error={formErrors.streetAddress1}
                onChange={(e) => onInputChange('streetAddress1', e.target.value)}
                autoComplete={`${autoCompletePrefix}address-line1`}
                fullWidth
                required
              />
              <Input
                id={`${idPrefix}streetAddress2`}
                placeholder={t('delivery.streetAddress2')}
                value={formData.streetAddress2 || ''}
                error={formErrors.streetAddress2}
                onChange={(e) => onInputChange('streetAddress2', e.target.value)}
                autoComplete={`${autoCompletePrefix}address-line2`}
                fullWidth
              />
            </div>

            <div className={styles.row}>
              {countryAreaField}
              <Input
                id={`${idPrefix}city`}
                placeholder={t('delivery.city')}
                value={formData.city}
                onChange={(e) => onInputChange('city', e.target.value)}
                error={formErrors.city}
                autoComplete={`${autoCompletePrefix}address-level2`}
                required
                fullWidth
              />
            </div>

            <div className={styles.row}>
              <Input
                id={`${idPrefix}postalCode`}
                placeholder={t('delivery.postalCode')}
                value={formData.postalCode}
                onChange={(e) => onInputChange('postalCode', e.target.value)}
                error={formErrors.postalCode}
                autoComplete={`${autoCompletePrefix}postal-code`}
                required
                fullWidth
              />
              {showCountry ? (
                <Select
                  id={`${idPrefix}country`}
                  placeholder={t('delivery.country')}
                  value={formData.country}
                  onChange={(e) => onInputChange('country', e.target.value)}
                  error={formErrors.country}
                  autoComplete={`${autoCompletePrefix}country`}
                  options={COUNTRIES.map((country) => ({
                    value: country.code,
                    label: country.name,
                  }))}
                  disabled={disableCountry}
                  required
                  fullWidth
                />
              ) : null}
            </div>
          </>
        )}

        <p className={styles.mandatoryNote}>{t('delivery.mandatoryFields')}</p>
        {footer}
      </Wrapper>
    );
  }
);

AddressForm.displayName = 'AddressForm';
